import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Badge from 'components/Badge';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import MobileDeliveryModal from 'components/MobileDeliveryModal';
import QuickActions from 'components/QuickActions';
import Toaster from 'components/Toaster';
import { useGetTrackTransaction } from 'components/Transactions/gql/useGetPurchase';
import { usePrintManager } from 'hooks/usePrintManager';
import { usePrintReceipt } from 'hooks/usePrintReceipt';
import { usePrintTickets } from 'hooks/usePrintTickets';
import { PrintingModal } from 'pages/app/Cart/Components/PrintingModal';
import { ReaderIpModal } from 'pages/app/Cart/Components/ReaderIpModal';
import { IconWrapper } from 'pages/track/Customers/components/Customers/StyledComponents';
import { SectionHeading } from 'pages/track/Customers/components/CustomerTable/StyledComponents';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
// Import reusable components from Customers
import TransactionDetailSkeleton from './TransactionDetailSkeleton';

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

// Extended Styled Components
const PageContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.primary
      : '#f2f2f2'};
  min-height: 100vh;
  padding: 48px 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    padding: 16px;
    overflow-x: hidden;
  }
`;

const Header = styled(Card)`
  background: ${(props) =>
    props.theme.card?.background ||
    (props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white')};
  border: 1px solid
    ${(props) =>
      props.theme.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.card?.shadow || 'rgba(0, 0, 0, 0.1) 0px 1px 3px'};
  padding: 32px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const OrderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const OrderNumber = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1E293B')};
  margin: 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CustomerName = styled.p`
  font-size: 17px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#64748b'};
  margin: 0;
  font-weight: 500;
  margin-top: 5px;
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;

  @media (max-width: 768px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const EventName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1E293B')};
  text-align: right;
  max-width: 600px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 16px;
    text-align: left;
    max-width: 100%;
  }
`;

const EventDate = styled.div`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#64748b'};
  font-weight: 500;
  text-align: right;

  @media (max-width: 768px) {
    font-size: 13px;
    text-align: left;
  }
`;

const Container = styled.div`
  /* Remove extra padding since PageContainer already has it */
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr 0.8fr;
  gap: 24px;
  margin-bottom: 24px;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 16px;
  }
`;

// Extend the shared Card with minor adjustments
const StyledCard = styled(Card)`
  margin-bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,0.95)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const CardTitle = styled(SectionHeading)`
  margin-bottom: 24px;
  position: relative;
  padding-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #fa4616;
    border-radius: 2px;
  }
`;

const FinancialRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: ${(props) =>
    props.isTotal || props.isLastBeforeTotal
      ? 'none'
      : `1px solid ${
          props.theme.name === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)'
        }`};
  ${(props) =>
    props.isTotal &&
    `
    padding-top: 16px;
    margin-top: 8px;
    border-top: 2px solid ${
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.3)'
        : 'rgba(250, 70, 22, 0.2)'
    };
    font-weight: 700;
  `}
`;

const FinancialLabel = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#64748B'};
  font-size: 14px;
  font-weight: ${(props) => (props.isTotal ? '600' : '400')};
`;

const FinancialValue = styled.span`
  color: ${(props) =>
    props.isTotal
      ? '#22c55e' // Profit is always green
      : props.positive
      ? '#22c55e' // Money IN (green)
      : props.moneyOut
      ? '#EF4444' // Money OUT (red)
      : props.neutral
      ? props.theme.name === 'dark'
        ? props.theme.colors.textGray.secondary
        : '#64748B' // Neutral info (gray)
      : props.theme.name === 'dark'
      ? props.theme.colors.textGray.dark
      : '#1F2937'}; // Default
  font-weight: ${(props) => (props.isTotal ? '700' : '600')};
  font-size: ${(props) => (props.isTotal ? '16px' : '14px')};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.05)'};
  transition: all 0.15s ease;

  &:hover {
    padding-left: 4px;
  }

  ${(props) => props.noBorder && 'border-bottom: none;'}
`;

const DetailLabel = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#64748B'};
  font-size: 14px;
  font-weight: 400;
`;

const DetailValue = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#94A3B8'};
  font-size: 14px;
`;

// Badge component moved to components/Badge
// Now using: <Badge uppercase> for default or <Badge outline uppercase> for promo codes

const ExternalLink = styled.a`
  color: #fa4616;
  text-decoration: none;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.15s ease;
  position: relative;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: #fa4616;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #e03a0d;

    &::after {
      width: 100%;
    }
  }
`;

const PassesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 0;
  }
`;

const PersonName = styled.h3`
  font-size: 18px;
  margin: 0 0 24px 0;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.primary : '#0f172a'};
  font-weight: 600;
  position: relative;
  padding-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #fa4616;
    border-radius: 2px;
  }
`;

// New Variation 1 Ticket Styles
const TicketCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(255, 255, 255, 0.95)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : props.theme.card?.border || 'rgb(226, 232, 240)'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  box-shadow: ${(props) =>
    props.theme.name === 'dark'
      ? 'none'
      : props.theme.card?.shadow || 'rgba(0, 0, 0, 0.05) 0px 1px 2px'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark' ? 'none' : 'rgba(0, 0, 0, 0.08) 0px 2px 4px'};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TicketInfo = styled.div`
  flex: 1;
`;

const TicketTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: 12px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: ${(props) => (props.isBundle ? 'pointer' : 'default')};
`;

const TicketTitleText = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BundleChevron = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#64748b'};
  font-size: 14px;
  transition: transform 0.2s;
  transform: ${(props) => (props.expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const BundleChildren = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'};
  overflow: hidden;
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  @keyframes slideDown {
    0% {
      opacity: 0;
      transform: translateY(-8px) scaleY(0.95);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scaleY(1);
    }
  }
`;

const ChildTicket = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(255,255,255,0.5)'};
  border-left: 3px solid #fa4616;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ChildTicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ChildTicketName = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.textGray.dark : '#1F2937'};
`;

const ChildTicketScanned = styled.span`
  color: #fa4616;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ChildTicketDetails = styled.div`
  display: flex;
  gap: 40px;
  font-size: 13px;
`;

const TicketDetails = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.hasSection
      ? '1.5fr 1fr 0.8fr 0.8fr 1.2fr 1fr' // Date | Section | Row | Seat | Barcode | Status
      : '1.5fr 1fr 1.5fr 1fr'}; // Date | Price | Barcode | Status
  gap: 20px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const TicketDetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const TicketDetailLabel = styled.span`
  font-size: 11px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.tertiary
      : props.theme.colors.textGray.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-weight: 600;
`;

const TicketDetailValue = styled.span`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.primary : '#1F2937'};
`;

const BarcodeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const BarcodeNumber = styled.span`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.primary : '#1F2937'};
  font-weight: 500;
`;

const BarcodeSuffix = styled.span`
  font-size: 12px;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.primary : '#ffffff'};
  font-weight: 700;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#475569' : '#64748b'};
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
`;

// TicketStatus component moved to components/Badge
// Now using: <Badge variant="accent" uppercase> with optional desktop/mobile props if needed
const TicketStatus = styled.div`
  display: inline-flex;

  @media (max-width: 768px) {
    display: ${(props) => (props.desktop ? 'none' : 'inline-flex')};
  }

  @media (min-width: 769px) {
    display: ${(props) => (props.mobile ? 'none' : 'inline-flex')};
  }
`;

// Two Column Layout for Tickets and Timeline
const TicketsAndTimelineGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    margin-top: 0;
  }
`;

// Timeline Section - Connected Line Style (Variation 1)
const TimelineSection = styled.div`
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

// TimelineContainer replaced with shared Card component

const TimelineHeader = styled.div`
  margin-bottom: 24px;
`;

const TimelineTitle = styled(SectionHeading)`
  margin-bottom: 0;
  position: relative;
  padding-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #fa4616;
    border-radius: 2px;
  }
`;

const TimelineList = styled.div`
  position: relative;
  padding-left: 60px;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 28px;
    bottom: 28px;
    width: 2px;
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.3)'
        : 'rgba(250, 70, 22, 0.2)'};
  }

  @media (max-width: 768px) {
    padding-left: 0;

    &::before {
      display: none;
    }
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -46px;
  top: 8px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fa4616;
  border: 2px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.3)'
        : 'rgba(250, 70, 22, 0.2)'};
  box-shadow: 0 0 0 4px
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.1)'
        : 'rgba(250, 70, 22, 0.05)'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const TimelineContent = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 6px;
  padding: 16px;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;

  &:hover {
    border-left-color: #fa4616;
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.05)'
        : 'rgba(250, 70, 22, 0.02)'};
  }
`;

const TimelineContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TimelineEventTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.primary : '#1F2937'};
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background: #fa4616;
    border-radius: 50%;
  }
`;

const TimelineTime = styled.div`
  font-size: 12px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.tertiary
      : '#9CA3AF'};
  white-space: nowrap;
`;

const TimelineDescription = styled.div`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : '#6B7280'};
  margin-bottom: 8px;
`;

const TimelineMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.tertiary
      : '#9CA3AF'};
`;

// StyledQuickActions is no longer needed - QuickActions now supports fullHeight prop

const TimelineAmount = styled.span`
  color: ${(props) =>
    props.type === 'positive'
      ? '#22c55e'
      : props.type === 'negative'
      ? '#ef4444'
      : '#fa4616'};
  font-weight: 600;
  font-size: 14px;
  padding: 2px 6px;
  background: ${(props) =>
    props.type === 'positive'
      ? 'rgba(34, 197, 94, 0.1)'
      : props.type === 'negative'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(250, 70, 22, 0.1)'};
  border-radius: 4px;
`;

// Helper function to parse monetary strings like "$20.00" to numbers
const parseMoneyString = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  // Remove dollar sign and any commas, then parse
  const cleaned = value.toString().replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

// Helper function to format barcode display
const formatBarcode = (barcode) => {
  if (!barcode) return '—';

  // Check if barcode has at least 4 characters for the suffix
  if (barcode.length <= 4) {
    return barcode;
  }

  // Split the barcode into main number and last 4 characters
  const mainNumber = barcode.slice(0, -4);
  const suffix = barcode.slice(-4);

  return (
    <BarcodeContainer>
      <BarcodeNumber>{mainNumber}</BarcodeNumber>
      <BarcodeSuffix>{suffix}</BarcodeSuffix>
    </BarcodeContainer>
  );
};

const TransactionDetailNew = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { data, loading } = useGetTrackTransaction(id, true);
  const transaction = data?.getTrackTransaction;

  const [toasterData, setToasterData] = useState(null);
  const [showMobileDeliveryModal, setShowMobileDeliveryModal] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isReaderIpVisible, setIsReaderIpVisible] = useState(false);
  const [pendingPrintAction, setPendingPrintAction] = useState(null);
  const [expandedBundles, setExpandedBundles] = useState({});

  // Get capabilities from AppContext
  const {
    state: { capabilities },
  } = useContext(AppContext);

  // Print hooks
  const { isConnected } = usePrintManager();
  const { printTickets } = usePrintTickets();
  const printReceiptFunction = usePrintReceipt();
  const { connectionStatus } = useCart();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check for success message from Issue Credit on mount
  useEffect(() => {
    const creditSuccessMessage = sessionStorage.getItem('creditSuccessMessage');
    if (creditSuccessMessage) {
      // Determine if it's a credit or refund from the message
      const isRefund = creditSuccessMessage.toLowerCase().includes('refund');
      setToasterData({
        title: isRefund ? 'Refund Issued!' : 'Credit Issued!',
        message: creditSuccessMessage,
        type: 'success',
      });
      // Clear the message from sessionStorage
      sessionStorage.removeItem('creditSuccessMessage');
    }
  }, []);

  // Run a pending print action once the printer is fully connected.
  // connectionStatus flips to 'Connected' only after createDevice completes,
  // which is after the modal auto-closes on SSL connect — so we can't rely on
  // the modal's visibility alone.
  useEffect(() => {
    if (!pendingPrintAction) return;
    if (connectionStatus !== 'Connected' || !isConnected()) return;
    if (pendingPrintAction === 'tickets') {
      printTickets({ transaction, setIsPrinting });
    } else if (pendingPrintAction === 'receipt') {
      printReceiptFunction({ transaction, setIsPrinting });
    }
    setPendingPrintAction(null);
  }, [
    connectionStatus,
    pendingPrintAction,
    isConnected,
    printTickets,
    printReceiptFunction,
    transaction,
  ]);

  // Clear a stale pending action if the modal closes without a successful
  // connection (cancel, error, etc.).
  useEffect(() => {
    if (isReaderIpVisible || !pendingPrintAction) return;
    const timer = setTimeout(() => {
      if (!isConnected()) setPendingPrintAction(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isReaderIpVisible, pendingPrintAction, isConnected]);

  // Determine the route prefix
  const routePrefix = location.pathname.includes('admin-track')
    ? '/admin-track'
    : location.pathname.includes('admin-employee')
    ? '/admin-employee'
    : '/admin';

  // Check if user is Admin (matches old component logic)
  const isAdmin = location.pathname.includes('/admin/');

  // Format date for timeline
  const formatCompactDate = (dateString) => {
    if (!dateString) return 'Date unavailable';
    // Handle timestamp numbers
    const timestamp =
      typeof dateString === 'string' ? Number(dateString) : dateString;

    // If timestamp has 10 or fewer digits, it's in seconds - convert to milliseconds
    const timestampMs =
      timestamp.toString().length <= 10 ? timestamp * 1000 : timestamp;

    const date = new Date(timestampMs);

    if (isNaN(date.getTime())) return 'Date unavailable';

    // Format like "Oct 28, 2024 • 3:45 PM EST"
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    return `${dateStr} • ${timeStr} EST`;
  };

  // Format event date for header
  const formatEventDate = (dateString) => {
    if (!dateString) return 'Event Date Unavailable';

    let parsedDate;

    // Check if it's an ISO date string (contains '-' or 'T')
    if (
      typeof dateString === 'string' &&
      (dateString.includes('-') || dateString.includes('T'))
    ) {
      parsedDate = new Date(dateString);
      // If not parseable, return as-is (for date ranges like "Jan 1 - 31")
      if (isNaN(parsedDate.getTime())) {
        return dateString;
      }
    } else if (
      typeof dateString === 'number' ||
      (typeof dateString === 'string' && /^\d+$/.test(dateString))
    ) {
      // It's a numeric timestamp - parse and check length
      const timestamp =
        typeof dateString === 'string' ? parseInt(dateString, 10) : dateString;

      // If timestamp has 10 or fewer digits, it's in seconds - convert to milliseconds
      const timestampMs =
        timestamp.toString().length <= 10 ? timestamp * 1000 : timestamp;

      parsedDate = new Date(timestampMs);
    } else {
      // Try parsing as a regular date string
      parsedDate = new Date(dateString);
    }

    // If still not parseable, return as-is
    if (isNaN(parsedDate.getTime())) {
      return dateString.toString();
    }

    // Format as "Friday, Jul 27, 2025"
    return parsedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) return <TransactionDetailSkeleton />;
  if (!transaction) return <div>Transaction not found</div>;

  // Check if all tickets in the transaction have been refunded
  const areAllTicketsRefunded = () => {
    if (!transaction.users) return false;

    let totalTickets = 0;
    let refundedTickets = 0;

    transaction.users.forEach((user) => {
      if (user.tickets) {
        user.tickets.forEach((ticket) => {
          totalTickets++;
          if (ticket.refunded) {
            refundedTickets++;
          }
        });
      }
    });

    return totalTickets > 0 && totalTickets === refundedTickets;
  };

  // Check if any tickets in the transaction have been credited
  const areAnyTicketsCredited = () => {
    if (!transaction.users) return false;

    return transaction.users.some((user) =>
      user.tickets?.some((ticket) => ticket.credited)
    );
  };

  // Calculate financial values - handle strings with $ signs
  const subtotal = parseMoneyString(transaction.subtotal);
  const discount = parseMoneyString(transaction.discount);
  const creditApplied = parseMoneyString(transaction.credit);
  const refundIssued = parseMoneyString(transaction.refunded_amount);
  const creditIssued = parseMoneyString(transaction.credits_issued);
  const fee = parseMoneyString(transaction.original_fee);
  const customerPaid = parseMoneyString(transaction.original_total);
  const profit = parseMoneyString(transaction.profit);

  // Format user name
  const userName = transaction.user
    ? `${transaction.user.last_name || ''}, ${
        transaction.user.first_name || ''
      }`.trim()
    : 'Guest User';

  // Toggle bundle expansion
  const toggleBundle = (ticketId) => {
    setExpandedBundles((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  return (
    <>
      <img
        id="logo"
        src="/images/th-simple-logo.png"
        width="200"
        height="100"
        style={{ display: 'none' }}
        alt="print-logo"
      />
      <EnhancedThemeProvider>
        <PageContainer>
          {toasterData && (
            <Toaster
              title={toasterData.title}
              message={toasterData.message}
              type={toasterData.type}
              onClose={() => setToasterData(null)}
            />
          )}
          <Header>
            <OrderInfo>
              <IconWrapper>
                <svg width="24" height="24" fill="#FA4616" viewBox="0 0 24 24">
                  <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </IconWrapper>
              <div>
                <OrderNumber>{userName}</OrderNumber>
                <CustomerName>Order # {transaction.id}</CustomerName>
              </div>
            </OrderInfo>
            {(transaction.track?.name ||
              (transaction.event && transaction.event.name)) && (
              <EventInfo>
                {transaction.track?.name && (
                  <EventDate>{transaction.track.name}</EventDate>
                )}
                {transaction.event && transaction.event.start_date && (
                  <EventDate>
                    {formatEventDate(transaction.event.start_date)}
                  </EventDate>
                )}
                {transaction.event && transaction.event.name && (
                  <EventName>{transaction.event.name}</EventName>
                )}
              </EventInfo>
            )}
          </Header>

          <Container>
            <MainGrid>
              <StyledCard>
                <CardTitle>Financial Breakdown</CardTitle>

                {[
                  {
                    label: 'Subtotal',
                    value: `$${subtotal.toFixed(2)}`,
                    positive: true, // Money IN
                  },
                  {
                    label: 'Discount',
                    value: `-$${discount.toFixed(2)}`,
                    moneyOut: discount > 0, // Money OUT
                  },
                  {
                    label: 'Credit Applied (used at checkout)',
                    value: `$${creditApplied.toFixed(2)}`,
                    neutral: true, // Just info
                  },
                  {
                    label: 'Refund Issued (to customer)',
                    value: `-$${refundIssued.toFixed(2)}`,
                    moneyOut: refundIssued > 0, // Money OUT
                  },
                  {
                    label: 'Credit Issued (to customer)',
                    value: `$${creditIssued.toFixed(2)}`,
                    moneyOut: creditIssued > 0, // Money OUT (even though positive number)
                  },
                  {
                    label: 'Fee',
                    value: `$${fee.toFixed(2)}`,
                    neutral: true, // Neutral
                  },
                  {
                    label: 'Customer Paid (at checkout)',
                    value: `$${customerPaid.toFixed(2)}`,
                    positive: true, // Money IN
                    isLastBeforeTotal: true,
                  },
                  {
                    label: 'Profit',
                    value: `$${profit.toFixed(2)}`,
                    isTotal: true, // Always green
                  },
                ].map((item, index) => (
                  <FinancialRow
                    key={index}
                    isTotal={item.isTotal}
                    isLastBeforeTotal={item.isLastBeforeTotal}
                  >
                    <FinancialLabel isTotal={item.isTotal}>
                      {item.label}
                    </FinancialLabel>
                    <FinancialValue
                      positive={item.positive}
                      neutral={item.neutral}
                      moneyOut={item.moneyOut}
                      isTotal={item.isTotal}
                    >
                      {item.value}
                    </FinancialValue>
                  </FinancialRow>
                ))}
              </StyledCard>

              <StyledCard>
                <CardTitle>Transaction Details</CardTitle>

                <DetailRow>
                  <DetailLabel>Order Source</DetailLabel>
                  <Badge uppercase>
                    {transaction.order_source || 'Unknown'}
                  </Badge>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Payment Method</DetailLabel>
                  <Badge uppercase>
                    {transaction.payment_method || 'Unknown'}
                  </Badge>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Pricing Type</DetailLabel>
                  <Badge
                    uppercase
                    variant={transaction.show_fee ? 'default' : 'accent'}
                  >
                    {transaction.show_fee ? 'Standard' : 'All-In-Pricing'}
                  </Badge>
                </DetailRow>

                {(() => {
                  // Check if we have valid promos to display
                  let promoToDisplay = null;

                  // First try transaction.promos
                  if (transaction.promos) {
                    if (Array.isArray(transaction.promos)) {
                      const validPromos = transaction.promos.filter(
                        (p) => p && p.trim()
                      );
                      if (validPromos.length > 0) {
                        promoToDisplay = validPromos.join(', ');
                      }
                    } else if (
                      typeof transaction.promos === 'string' &&
                      transaction.promos.trim()
                    ) {
                      promoToDisplay = transaction.promos;
                    }
                  }

                  // Fallback to users[0].promo if promos is empty
                  if (!promoToDisplay && transaction.users?.[0]?.promo) {
                    promoToDisplay = transaction.users[0].promo;
                  }

                  // Only render if we have a valid promo to display
                  if (promoToDisplay) {
                    return (
                      <DetailRow>
                        <DetailLabel>Promo Code</DetailLabel>
                        <Badge outline uppercase>
                          {promoToDisplay}
                        </Badge>
                      </DetailRow>
                    );
                  }

                  return null;
                })()}

                {transaction.order_source === 'pos' && transaction.register && (
                  <DetailRow>
                    <DetailLabel>Sold By</DetailLabel>
                    <span style={{ fontWeight: 500 }}>
                      {transaction.register}
                    </span>
                  </DetailRow>
                )}

                {isAdmin && (
                  <DetailRow>
                    <DetailLabel>Transfer</DetailLabel>
                    {transaction.transfer_id ? (
                      <ExternalLink
                        href={transaction.transfer_id}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </ExternalLink>
                    ) : (
                      <DetailValue>N/A</DetailValue>
                    )}
                  </DetailRow>
                )}

                {isAdmin && (
                  <DetailRow noBorder>
                    <DetailLabel>Charge</DetailLabel>
                    {transaction.charge_id ? (
                      <ExternalLink
                        href={transaction.charge_id}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </ExternalLink>
                    ) : (
                      <DetailValue>N/A</DetailValue>
                    )}
                  </DetailRow>
                )}
              </StyledCard>

              <QuickActions
                title="Actions"
                fullHeight
                accentTitle
                actions={[
                  // Show Refund button based on capabilities
                  // Only show if user has permission (isAdmin OR issue_refunds capability)
                  // Disabled if all tickets refunded AND none are credited
                  // Credits CAN be converted to refunds, so enabled when tickets are credited
                  ...(isAdmin || (capabilities && capabilities?.issue_refunds)
                    ? [
                        {
                          label: 'Issue Refund',
                          icon: (
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                              />
                            </svg>
                          ),
                          onClick: () => {
                            // Navigate to Transaction Refund page with transaction ID and return path
                            const userId = transaction.user?.id;
                            const returnPath = `${routePrefix}/transactions/detail/${id}`;
                            // Only include customerId if we have a valid userId
                            const customerIdParam = userId
                              ? `&customerId=${userId}`
                              : '';
                            history.replace(
                              `${routePrefix}/transactions/${id}/refund?returnTo=${encodeURIComponent(
                                returnPath
                              )}${customerIdParam}`
                            );
                          },
                          disabled:
                            areAllTicketsRefunded() && !areAnyTicketsCredited(),
                          disabledReason:
                            areAllTicketsRefunded() && !areAnyTicketsCredited()
                              ? 'All tickets have already been refunded'
                              : undefined,
                        },
                      ]
                    : []),
                  // Show Credit button based on capabilities
                  // Only show if user has permission (isAdmin OR issue_credits capability)
                  // Disabled if all tickets are refunded
                  // Refunds CANNOT be converted to credits
                  ...(isAdmin || (capabilities && capabilities?.issue_credits)
                    ? [
                        {
                          label: 'Issue Credit',
                          icon: (
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                            </svg>
                          ),
                          onClick: () => {
                            // Navigate to Transaction Credit page with transaction ID and return path
                            const userId = transaction.user?.id;
                            const returnPath = `${routePrefix}/transactions/detail/${id}`;
                            // Only include customerId if we have a valid userId
                            const customerIdParam = userId
                              ? `&customerId=${userId}`
                              : '';
                            history.replace(
                              `${routePrefix}/transactions/${id}/credit?returnTo=${encodeURIComponent(
                                returnPath
                              )}${customerIdParam}`
                            );
                          },
                          disabled: areAllTicketsRefunded(),
                          disabledReason: areAllTicketsRefunded()
                            ? 'Cannot issue credit for fully refunded transactions'
                            : undefined,
                        },
                      ]
                    : []),
                  ...(transaction?.event?.pos_settings?.ticket_delivery ===
                  'mobile'
                    ? [
                        {
                          label: 'Deliver to Mobile',
                          icon: (
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h6zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H5z" />
                              <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
                            </svg>
                          ),
                          onClick: () => {
                            setShowMobileDeliveryModal(true);
                          },
                        },
                      ]
                    : []),
                  ...(transaction?.event?.pos_settings?.ticket_delivery ===
                  'print'
                    ? [
                        {
                          label: `Print ${
                            getTicketText(2).charAt(0).toUpperCase() +
                            getTicketText(2).slice(1)
                          }`,
                          icon: (
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
                              <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z" />
                            </svg>
                          ),
                          onClick: () => {
                            if (!isConnected()) {
                              setPendingPrintAction('tickets');
                              setIsReaderIpVisible(true);
                              return;
                            }
                            printTickets({ transaction, setIsPrinting });
                          },
                        },
                      ]
                    : []),
                  ...(transaction?.event?.pos_settings?.receipt_delivery ===
                  'print'
                    ? [
                        {
                          label: 'Print Receipt',
                          icon: (
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M1.92.506a.5.5 0 0 1 .434.14L3 1.293l.646-.647a.5.5 0 0 1 .708 0L5 1.293l.646-.647a.5.5 0 0 1 .708 0L7 1.293l.646-.647a.5.5 0 0 1 .708 0L9 1.293l.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .801.13l.5 1A.5.5 0 0 1 15 2v12a.5.5 0 0 1-.053.224l-.5 1a.5.5 0 0 1-.8.13L13 14.707l-.646.647a.5.5 0 0 1-.708 0L11 14.707l-.646.647a.5.5 0 0 1-.708 0L9 14.707l-.646.647a.5.5 0 0 1-.708 0L7 14.707l-.646.647a.5.5 0 0 1-.708 0L5 14.707l-.646.647a.5.5 0 0 1-.708 0L3 14.707l-.646.647a.5.5 0 0 1-.801-.13l-.5-1A.5.5 0 0 1 1 14V2a.5.5 0 0 1 .053-.224l.5-1a.5.5 0 0 1 .367-.27zm.217 1.338L2 2.118v11.764l.137.274.51-.51a.5.5 0 0 1 .707 0l.646.647.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.509.509.137-.274V2.118l-.137-.274-.51.51a.5.5 0 0 1-.707 0L12 1.707l-.646.647a.5.5 0 0 1-.708 0L10 1.707l-.646.647a.5.5 0 0 1-.708 0L8 1.707l-.646.647a.5.5 0 0 1-.708 0L6 1.707l-.646.647a.5.5 0 0 1-.708 0L4 1.707l-.646.647a.5.5 0 0 1-.708 0l-.509-.51z"
                              />
                              <path
                                fillRule="evenodd"
                                d="M3 4.5a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5zm8-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5z"
                              />
                            </svg>
                          ),
                          onClick: () => {
                            if (!isConnected()) {
                              setPendingPrintAction('receipt');
                              setIsReaderIpVisible(true);
                              return;
                            }
                            printReceiptFunction({
                              transaction,
                              setIsPrinting,
                            });
                          },
                        },
                      ]
                    : []),
                  ...(transaction?.user?.id
                    ? [
                        {
                          label: isAdmin ? 'View User' : 'View Customer',
                          icon: (
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                            </svg>
                          ),
                          onClick: () => {
                            const userRoutePrefix = isAdmin
                              ? '/admin'
                              : '/admin-employee';
                            history.push(
                              `${userRoutePrefix}/users/transactions/${transaction.user.id}`
                            );
                          },
                        },
                      ]
                    : []),
                ]}
              />
            </MainGrid>

            <TicketsAndTimelineGrid>
              <PassesGrid>
                {transaction.users?.map((user, userIndex) => (
                  <StyledCard key={userIndex}>
                    <PersonName>
                      {user.user?.first_name || user.user?.last_name
                        ? `${user.user?.first_name || ''} ${
                            user.user?.last_name || ''
                          }`.trim()
                        : getTicketText(2).charAt(0).toUpperCase() +
                          getTicketText(2).slice(1)}
                    </PersonName>

                    {user.tickets?.map((ticket, ticketIndex) => {
                      const isBundle = ticket.type === 'bundle';
                      const isExpanded = expandedBundles[ticket.id];

                      return (
                        <TicketCard key={ticketIndex}>
                          <TicketInfo>
                            <TicketTitle
                              isBundle={isBundle}
                              onClick={() =>
                                isBundle && toggleBundle(ticket.id)
                              }
                            >
                              <TicketTitleText>
                                {isBundle && (
                                  <BundleChevron expanded={isExpanded}>
                                    ▶
                                  </BundleChevron>
                                )}
                                <span>
                                  {ticket.name ||
                                    ticket.type ||
                                    'General Admission'}
                                </span>
                              </TicketTitleText>
                              {(ticket.refunded ||
                                ticket.credited ||
                                (ticket.scan_logs &&
                                  ticket.scan_logs.length > 0)) && (
                                <TicketStatus desktop>
                                  {ticket.credited ? (
                                    <Badge
                                      variant="accent"
                                      uppercase
                                      icon={
                                        <svg
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                        </svg>
                                      }
                                    >
                                      Credited
                                    </Badge>
                                  ) : ticket.refunded ? (
                                    <Badge
                                      variant="accent"
                                      uppercase
                                      icon={
                                        <svg
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                          />
                                        </svg>
                                      }
                                    >
                                      Refunded
                                    </Badge>
                                  ) : (
                                    ticket.scan_logs &&
                                    ticket.scan_logs.length > 0 && (
                                      <Badge
                                        variant="accent"
                                        uppercase
                                        icon={
                                          <svg
                                            width="16"
                                            height="16"
                                            fill="currentColor"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                                          </svg>
                                        }
                                      >
                                        Scanned
                                      </Badge>
                                    )
                                  )}
                                </TicketStatus>
                              )}
                            </TicketTitle>
                            <TicketDetails hasSection={!!ticket.section}>
                              <TicketDetailItem>
                                <TicketDetailLabel>
                                  {getTicketText(1).charAt(0).toUpperCase() +
                                    getTicketText(1).slice(1)}{' '}
                                  Date
                                </TicketDetailLabel>
                                <TicketDetailValue>
                                  {ticket.date || 'Event Date'}
                                </TicketDetailValue>
                              </TicketDetailItem>
                              {ticket.section ? (
                                <>
                                  <TicketDetailItem>
                                    <TicketDetailLabel>
                                      Section
                                    </TicketDetailLabel>
                                    <TicketDetailValue>
                                      {ticket.section}
                                    </TicketDetailValue>
                                  </TicketDetailItem>
                                  <TicketDetailItem>
                                    <TicketDetailLabel>Row</TicketDetailLabel>
                                    <TicketDetailValue>
                                      {ticket.row || '—'}
                                    </TicketDetailValue>
                                  </TicketDetailItem>
                                  <TicketDetailItem>
                                    <TicketDetailLabel>Seat</TicketDetailLabel>
                                    <TicketDetailValue>
                                      {ticket.seat || '—'}
                                    </TicketDetailValue>
                                  </TicketDetailItem>
                                  {!isBundle &&
                                    process.env.REACT_APP_PLATFORM ===
                                      'tickethoss' && (
                                      <TicketDetailItem>
                                        <TicketDetailLabel>
                                          Serial Number
                                        </TicketDetailLabel>
                                        <TicketDetailValue>
                                          {formatBarcode(ticket.barcode)}
                                        </TicketDetailValue>
                                      </TicketDetailItem>
                                    )}
                                  {(ticket.refunded ||
                                    ticket.credited ||
                                    (ticket.scan_logs &&
                                      ticket.scan_logs.length > 0)) && (
                                    <TicketDetailItem
                                      style={{ alignSelf: 'end' }}
                                    >
                                      <TicketStatus mobile>
                                        {ticket.credited ? (
                                          <Badge
                                            variant="accent"
                                            uppercase
                                            icon={
                                              <svg
                                                width="16"
                                                height="16"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                              </svg>
                                            }
                                          >
                                            Credited
                                          </Badge>
                                        ) : ticket.refunded ? (
                                          <Badge
                                            variant="accent"
                                            uppercase
                                            icon={
                                              <svg
                                                width="16"
                                                height="16"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                                />
                                              </svg>
                                            }
                                          >
                                            Refunded
                                          </Badge>
                                        ) : (
                                          ticket.scan_logs &&
                                          ticket.scan_logs.length > 0 && (
                                            <Badge
                                              variant="accent"
                                              uppercase
                                              icon={
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  fill="currentColor"
                                                  viewBox="0 0 16 16"
                                                >
                                                  <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                                                </svg>
                                              }
                                            >
                                              Scanned
                                            </Badge>
                                          )
                                        )}
                                      </TicketStatus>
                                    </TicketDetailItem>
                                  )}
                                </>
                              ) : (
                                <>
                                  <TicketDetailItem>
                                    <TicketDetailLabel>Price</TicketDetailLabel>
                                    <TicketDetailValue>
                                      $
                                      {parseMoneyString(ticket.price).toFixed(
                                        2
                                      )}
                                    </TicketDetailValue>
                                  </TicketDetailItem>
                                  {!isBundle &&
                                    process.env.REACT_APP_PLATFORM ===
                                      'tickethoss' && (
                                      <TicketDetailItem>
                                        <TicketDetailLabel>
                                          Serial Number
                                        </TicketDetailLabel>
                                        <TicketDetailValue>
                                          {formatBarcode(ticket.barcode)}
                                        </TicketDetailValue>
                                      </TicketDetailItem>
                                    )}
                                  {(ticket.refunded ||
                                    ticket.credited ||
                                    (ticket.scan_logs &&
                                      ticket.scan_logs.length > 0)) && (
                                    <TicketDetailItem
                                      style={{ alignSelf: 'end' }}
                                    >
                                      <TicketStatus mobile>
                                        {ticket.credited ? (
                                          <Badge
                                            variant="accent"
                                            uppercase
                                            icon={
                                              <svg
                                                width="16"
                                                height="16"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                              </svg>
                                            }
                                          >
                                            Credited
                                          </Badge>
                                        ) : ticket.refunded ? (
                                          <Badge
                                            variant="accent"
                                            uppercase
                                            icon={
                                              <svg
                                                width="16"
                                                height="16"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                                />
                                              </svg>
                                            }
                                          >
                                            Refunded
                                          </Badge>
                                        ) : (
                                          ticket.scan_logs &&
                                          ticket.scan_logs.length > 0 && (
                                            <Badge
                                              variant="accent"
                                              uppercase
                                              icon={
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  fill="currentColor"
                                                  viewBox="0 0 16 16"
                                                >
                                                  <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                                                </svg>
                                              }
                                            >
                                              Scanned
                                            </Badge>
                                          )
                                        )}
                                      </TicketStatus>
                                    </TicketDetailItem>
                                  )}
                                </>
                              )}
                            </TicketDetails>

                            {/* Bundle Children */}
                            {isBundle &&
                              isExpanded &&
                              ticket.bundle_children &&
                              ticket.bundle_children.length > 0 && (
                                <BundleChildren>
                                  {ticket.bundle_children.map(
                                    (child, childIndex) => (
                                      <ChildTicket key={childIndex}>
                                        <ChildTicketHeader>
                                          <ChildTicketName>
                                            {child.name ||
                                              getTicketText(1)
                                                .charAt(0)
                                                .toUpperCase() +
                                                getTicketText(1).slice(1)}
                                          </ChildTicketName>
                                          {child.scan_logs &&
                                          child.scan_logs.length > 0 ? (
                                            <ChildTicketScanned>
                                              ✓ Scanned
                                            </ChildTicketScanned>
                                          ) : (
                                            <span
                                              style={{
                                                color: '#64748b',
                                                fontSize: '11px',
                                              }}
                                            >
                                              Not Scanned
                                            </span>
                                          )}
                                        </ChildTicketHeader>
                                        <ChildTicketDetails>
                                          <TicketDetailItem>
                                            <TicketDetailLabel>
                                              {getTicketText(1)
                                                .charAt(0)
                                                .toUpperCase() +
                                                getTicketText(1).slice(1)}{' '}
                                              Date
                                            </TicketDetailLabel>
                                            <TicketDetailValue>
                                              {child.date || 'Event Date'}
                                            </TicketDetailValue>
                                          </TicketDetailItem>
                                          {process.env.REACT_APP_PLATFORM ===
                                            'tickethoss' && (
                                            <TicketDetailItem>
                                              <TicketDetailLabel>
                                                Serial Number
                                              </TicketDetailLabel>
                                              <TicketDetailValue>
                                                {formatBarcode(child.barcode)}
                                              </TicketDetailValue>
                                            </TicketDetailItem>
                                          )}
                                        </ChildTicketDetails>
                                      </ChildTicket>
                                    )
                                  )}
                                </BundleChildren>
                              )}
                          </TicketInfo>
                        </TicketCard>
                      );
                    })}
                  </StyledCard>
                ))}
              </PassesGrid>

              <TimelineSection>
                <Card title="Transaction History" accentTitle>
                  <TimelineHeader style={{ display: 'none' }}>
                    <TimelineTitle>Transaction History</TimelineTitle>
                  </TimelineHeader>
                  <TimelineList>
                    {(() => {
                      // Combine all events into one chronological array
                      const timelineEvents = [];

                      // Add purchase event
                      if (transaction.purchase_date) {
                        timelineEvents.push({
                          type: 'purchase',
                          timestamp: transaction.purchase_date,
                          data: {
                            total: transaction.original_total,
                            creditUsed: transaction.credit,
                            paymentMethod: transaction.payment_method,
                            ticketCount:
                              transaction.users?.reduce(
                                (count, user) =>
                                  count + (user.tickets?.length || 0),
                                0
                              ) || 0,
                          },
                        });
                      }

                      // Add credits
                      if (transaction.credits) {
                        transaction.credits.forEach((credit) => {
                          timelineEvents.push({
                            type: 'credit',
                            timestamp: credit.created_at,
                            data: credit,
                          });
                        });
                      }

                      // Add refunds
                      if (transaction.refunds) {
                        transaction.refunds.forEach((refund) => {
                          timelineEvents.push({
                            type: 'refund',
                            timestamp: refund.created_at,
                            data: refund,
                          });
                        });
                      }

                      // Add scan logs
                      if (transaction.users) {
                        transaction.users.forEach((user) => {
                          if (user.tickets) {
                            user.tickets.forEach((ticket) => {
                              if (ticket.scan_logs) {
                                ticket.scan_logs.forEach((scan) => {
                                  timelineEvents.push({
                                    type: 'scan',
                                    timestamp:
                                      scan.raw_created_at || scan.created_at,
                                    data: {
                                      ...scan,
                                      ticketName: ticket.name,
                                      ticketBarcode: ticket.barcode,
                                    },
                                  });
                                });
                              }
                            });
                          }
                        });
                      }

                      // Debug: Log all events before sorting
                      // console.log(
                      //   'Timeline events before sorting:',
                      //   timelineEvents.map((e) => ({
                      //     type: e.type,
                      //     timestamp: e.timestamp,
                      //     id: e.data?.id,
                      //   }))
                      // );

                      // Sort events by timestamp
                      timelineEvents.sort((a, b) => {
                        // Handle missing timestamps
                        if (!a.timestamp && !b.timestamp) return 0;
                        if (!a.timestamp) return 1; // Put items without timestamp at the end
                        if (!b.timestamp) return -1;

                        // Convert timestamps to numbers if they're strings
                        const timestampA =
                          typeof a.timestamp === 'string'
                            ? Number(a.timestamp)
                            : a.timestamp;
                        const timestampB =
                          typeof b.timestamp === 'string'
                            ? Number(b.timestamp)
                            : b.timestamp;

                        const dateA = new Date(timestampA);
                        const dateB = new Date(timestampB);

                        // Check for invalid dates
                        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime()))
                          return 0;
                        if (isNaN(dateA.getTime())) return 1;
                        if (isNaN(dateB.getTime())) return -1;

                        return dateA - dateB;
                      });

                      // Debug: Log all events after sorting
                      // console.log(
                      //   'Timeline events after sorting:',
                      //   timelineEvents.map((e) => ({
                      //     type: e.type,
                      //     timestamp: e.timestamp,
                      //     id: e.data?.id,
                      //     date: e.timestamp
                      //       ? new Date(
                      //           typeof e.timestamp === 'string'
                      //             ? Number(e.timestamp)
                      //             : e.timestamp
                      //         ).toLocaleString()
                      //       : 'no timestamp',
                      //   }))
                      // );

                      // Render timeline events
                      return timelineEvents.map((event, index) => (
                        <TimelineItem key={`timeline-${event.type}-${index}`}>
                          <TimelineDot />
                          <TimelineContent>
                            {event.type === 'purchase' && (
                              <>
                                <TimelineContentHeader>
                                  <TimelineEventTitle>
                                    Purchase Completed
                                  </TimelineEventTitle>
                                  <TimelineTime>
                                    {formatCompactDate(event.timestamp)}
                                  </TimelineTime>
                                </TimelineContentHeader>
                                <TimelineDescription>
                                  Transaction processed successfully
                                </TimelineDescription>
                                <TimelineMeta>
                                  <span>
                                    Method: {event.data.paymentMethod}
                                  </span>
                                  <span>
                                    {getTicketText(event.data.ticketCount)
                                      .charAt(0)
                                      .toUpperCase() +
                                      getTicketText(
                                        event.data.ticketCount
                                      ).slice(1)}
                                    : {event.data.ticketCount}
                                  </span>
                                  {(() => {
                                    const creditUsed = parseMoneyString(
                                      event.data.creditUsed
                                    );
                                    const total = parseMoneyString(
                                      event.data.total
                                    );

                                    // If credit was used, show both credit and payment method
                                    if (creditUsed > 0) {
                                      const paymentAmount = total;
                                      // Capitalize first letter of payment method
                                      const paymentMethodLabel = event.data
                                        .paymentMethod
                                        ? event.data.paymentMethod
                                            .charAt(0)
                                            .toUpperCase() +
                                          event.data.paymentMethod.slice(1)
                                        : 'Payment';
                                      return (
                                        <>
                                          <TimelineAmount type="positive">
                                            Credit: ${creditUsed.toFixed(2)}
                                          </TimelineAmount>
                                          <TimelineAmount>
                                            {paymentMethodLabel}: $
                                            {paymentAmount.toFixed(2)}
                                          </TimelineAmount>
                                        </>
                                      );
                                    }

                                    // Otherwise, just show the total
                                    return (
                                      <TimelineAmount>
                                        ${total.toFixed(2)}
                                      </TimelineAmount>
                                    );
                                  })()}
                                </TimelineMeta>
                              </>
                            )}

                            {event.type === 'credit' && (
                              <>
                                <TimelineContentHeader>
                                  <TimelineEventTitle>
                                    Credit Issued
                                  </TimelineEventTitle>
                                  <TimelineTime>
                                    {formatCompactDate(event.timestamp)}
                                  </TimelineTime>
                                </TimelineContentHeader>
                                <TimelineDescription>
                                  {event.data.reason ||
                                    'Customer service adjustment - Applied to account'}
                                </TimelineDescription>
                                <TimelineMeta>
                                  <span>Credit ID: {event.data.id}</span>
                                  <span>
                                    Issued by:{' '}
                                    {event.data.created_by?.first_name}{' '}
                                    {event.data.created_by?.last_name}
                                  </span>
                                  <TimelineAmount type="positive">
                                    +$
                                    {parseMoneyString(
                                      event.data.original_amount
                                    ).toFixed(2)}
                                  </TimelineAmount>
                                </TimelineMeta>
                              </>
                            )}

                            {event.type === 'refund' && (
                              <>
                                <TimelineContentHeader>
                                  <TimelineEventTitle>
                                    Refund Issued
                                  </TimelineEventTitle>
                                  <TimelineTime>
                                    {formatCompactDate(event.timestamp)}
                                  </TimelineTime>
                                </TimelineContentHeader>
                                <TimelineDescription>
                                  {event.data.reason ||
                                    'Customer service adjustment - Refund processed'}
                                </TimelineDescription>
                                <TimelineMeta>
                                  <span>Refund ID: {event.data.id}</span>
                                  <span>
                                    Issued by:{' '}
                                    {event.data.created_by?.first_name}{' '}
                                    {event.data.created_by?.last_name}
                                  </span>
                                  <TimelineAmount type="negative">
                                    -$
                                    {parseMoneyString(
                                      event.data.amount
                                    ).toFixed(2)}
                                  </TimelineAmount>
                                </TimelineMeta>
                              </>
                            )}

                            {event.type === 'scan' && (
                              <>
                                <TimelineContentHeader>
                                  <TimelineEventTitle>
                                    {getTicketText(1).charAt(0).toUpperCase() +
                                      getTicketText(1).slice(1)}{' '}
                                    Checked{' '}
                                    {event.data.status === 'in' ? 'In' : 'Out'}
                                  </TimelineEventTitle>
                                  <TimelineTime>
                                    {formatCompactDate(event.timestamp)}
                                  </TimelineTime>
                                </TimelineContentHeader>
                                <TimelineDescription>
                                  {event.data.ticketName} -
                                  {formatBarcode(event.data.ticketBarcode)}
                                </TimelineDescription>
                                <TimelineMeta>
                                  <span>
                                    Scanned by: {event.data.scanner?.first_name}{' '}
                                    {event.data.scanner?.last_name}
                                  </span>
                                  {event.data.status && (
                                    <span>Status: {event.data.status}</span>
                                  )}
                                </TimelineMeta>
                              </>
                            )}
                          </TimelineContent>
                        </TimelineItem>
                      ));
                    })()}
                  </TimelineList>
                </Card>
              </TimelineSection>
            </TicketsAndTimelineGrid>
          </Container>
        </PageContainer>
        {showMobileDeliveryModal && transaction && (
          <MobileDeliveryModal
            isOpen={showMobileDeliveryModal}
            onClose={() => setShowMobileDeliveryModal(false)}
            transaction={transaction}
            onSuccess={() => {
              setToasterData({
                title: 'Success!',
                message: `${
                  getTicketText(2).charAt(0).toUpperCase() +
                  getTicketText(2).slice(1)
                } have been delivered successfully via SMS.`,
                type: 'success',
              });
            }}
          />
        )}
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData?.title || ''}
          message={toasterData?.message || ''}
          type={toasterData?.type || 'info'}
          duration={8000}
        />
      </EnhancedThemeProvider>
      <ReaderIpModal
        isVisible={isReaderIpVisible}
        setIsVisible={setIsReaderIpVisible}
      />
      <PrintingModal isVisible={isPrinting} />
    </>
  );
};

export default TransactionDetailNew;
